import Link from "next/link"
import dynamic from "next/dynamic"
import { useCopy } from "@/lib/contexts/appContext"
import { SiteLogo } from "@/common/logos/siteLogo"

import Menu from "./menu"
import styles from "./footer.module.css"
import classNames from "classnames"

export default function Footer() {
  const copy = useCopy()
  const { english } = copy.header ?? {}
  const { columns, cookies, privacy, hosting, menu, publisher, socials, year } =
    copy.footer ?? {}
  const sectionSum = [
    columns?.length,
    socials?.links ? 1 : 0,
    menu ? 1 : 0
  ].reduce((acc, cur) => acc + cur, 0)
  const sectionItems = isNaN(sectionSum) ? 0 : sectionSum

  return (
    <footer className={classNames(styles.footer)}>
      <div className="inner-wrapper">
        <div className="logos">
          <SiteLogo />
        </div>
        {sectionItems > 0 && (
          <div className={classNames("section", "columns-" + sectionItems)}>
            {columns &&
              columns.map((column, index) => (
                <Column key={index} column={column} />
              ))}
            <Menu heading={menu} />
            {socials?.links && <Social {...{ socials }} />}
          </div>
        )}
        {hosting && (
          <div
            className="hosting"
            dangerouslySetInnerHTML={{ __html: hosting }}
          />
        )}
        <div
          className={classNames({
            "bottom-row": true,
            "has-hosting": !!hosting
          })}>
          <span className="publisher">
            &copy; {year + " "}
            {publisher && (
              <Link
                href={publisher?.url}
                target={publisher?.target}
                prefetch={false}>
                {publisher?.title}
              </Link>
            )}
          </span>
          <Links links={[privacy, cookies, english]} />
        </div>
      </div>
    </footer>
  )
}

const Column = ({ column }) => {
  return (
    <div className="column">
      <h4>{column?.heading}</h4>
      <div
        className="content"
        dangerouslySetInnerHTML={{ __html: column?.content }}
      />
    </div>
  )
}

const Social = ({ socials }) => {
  const { heading, links } = socials
  return (
    <div className="column socials">
      {heading && <h4>{heading}</h4>}
      <ul>
        {links.map((link) => {
          return (
            <li className={link.service} key={link.url}>
              <Link
                href={link.url}
                prefetch={false}
                rel="noreferrer noopener"
                target="_blank">
                <Icon service={link.service} />
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const Links = ({ links }) => {
  return (
    <ul className="links">
      {links.filter(Boolean).map((link) => {
        if (!link?.url) return null

        return (
          <li key={link.url}>
            <Link
              href={link.url}
              prefetch={false}
              rel="noreferrer noopener"
              target={link?.target ?? "_self"}>
              {link.title ?? link.label ?? null}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

const Icon = ({ service, ...props }) => {
  const Icon = icons[service] ?? null
  return <Icon {...props} />
}

const icons = {
  facebook: dynamic(() => import("@/components/common/icons/facebook")),
  instagram: dynamic(() => import("@/components/common/icons/instagram")),
  linkedin: dynamic(() => import("@/components/common/icons/linkedin")),
  tiktok: dynamic(() => import("@/components/common/icons/tiktok")),
  x: dynamic(() => import("@/components/common/icons/x")),
  youtube: dynamic(() => import("@/components/common/icons/youtube"))
}
